import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';
import { TranslocoService } from '@ngneat/transloco';
import { DatalogChannelsArtifact } from 'src/app/model/response/ai-chat-response';
import {
  baseChannelName,
  currentChannelLocale,
  resolveChannelLabel,
} from 'src/app/model/channel-label';
import { formatTruncated } from 'src/app/shared/number-format.util';

const VISION_PALETTE = [
  '#00FF00',
  '#FF0000',
  '#FF8A00',
  '#F2C94C',
  '#9B51E0',
  '#2D9CDB',
  '#27AE60',
  '#FFFFFF',
  '#7DBFFF',
  '#C88CF0',
];

const RPM_CHANNELS = new Set(['RPM', 'RPM_Control', 'RPM_LoRes', 'RPM_HiRes', 'RPMIndex']);

const RPM_COLOR = VISION_PALETTE[0];

const ALERT_COLOR = VISION_PALETTE[1];

const COMMENT_COLOR = VISION_PALETTE[5];

const RESERVED_COLORS = new Set([RPM_COLOR, ALERT_COLOR, COMMENT_COLOR, VISION_PALETTE[8]]);

const CHANNEL_PALETTE = VISION_PALETTE.filter((c) => !RESERVED_COLORS.has(c));

/**
 * Rodapé do card: canais ausentes e, se houve decimação, a nota genérica.
 *
 * Contrato datalog-channels §9 e CHART_PRESENTATION_FRONTEND item 1: contagens de pontos são
 * metadado técnico e ficam fora da UI. `downsampled_to` continua no payload/histórico; na tela
 * vira só "Visualização resumida para facilitar a leitura." — a mesma frase, e a mesma chave,
 * dos outros renderers de gráfico do chat.
 */
export function datalogFooterNote(
  data: Pick<DatalogChannelsArtifact, 'missing_channels' | 'downsampled_to'> | null | undefined,
  translate: (key: string, params?: Record<string, unknown>) => string,
  labelFor: (channel: string) => string,
): string {
  const parts: string[] = [];
  if (data?.missing_channels?.length) {
    // Passa pelo dicionário: o payload traz o nome técnico do canal pedido.
    const list = data.missing_channels.map(labelFor).join(', ');
    parts.push(translate('chatbot.datalogChart.missingChannels', { list }));
  }
  if (data?.downsampled_to) {
    parts.push(translate('chatbot.chart.reducedNote'));
  }
  return parts.join(' · ');
}

@Component({
  selector: 'app-datalog-chart',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="datalog-chart">
      <div class="datalog-chart-header" *ngIf="data?.file_name">
        <i class="pi pi-chart-line"></i>
        <span>{{ data.file_name }}</span>
        <span class="datalog-chart-meta">{{ channelNames }} · {{ durationLabel }}</span>
      </div>
      <div #chart class="datalog-chart-canvas"></div>
      <div class="datalog-chart-events" *ngIf="markerRows.length">
        <button type="button" class="datalog-chart-events-toggle" (click)="expanded = !expanded">
          <i class="pi" [ngClass]="expanded ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
          <span>{{ eventsSummary }}</span>
        </button>
        <ul class="datalog-chart-events-list" *ngIf="expanded">
          <li *ngFor="let row of markerRows" [ngClass]="row.kind">
            <span class="datalog-chart-events-time">{{ row.time }}</span>
            <span class="datalog-chart-events-dot"></span>
            <span class="datalog-chart-events-label">{{ row.label }}</span>
          </li>
        </ul>
      </div>
      <div class="datalog-chart-note" *ngIf="markersUnavailableNote">
        <i class="pi pi-info-circle"></i>
        <span>{{ markersUnavailableNote }}</span>
      </div>
      <div class="datalog-chart-footer" *ngIf="footerNote">{{ footerNote }}</div>
    </div>
  `,
  styles: [`
    .datalog-chart {
      margin: 8px 0;
      border: 1px solid #303030;
      border-radius: 8px;
      background: #0b0c0e;
      overflow: hidden;
    }
    .datalog-chart-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      border-bottom: 1px solid #303030;
      color: #d5d5d5;
      font-size: 12px;
    }
    .datalog-chart-meta { color: #9b9b9b; margin-left: auto; }
    .datalog-chart-canvas { width: 100%; height: 380px; }
    .datalog-chart-footer {
      padding: 6px 12px;
      border-top: 1px solid #303030;
      color: #9b9b9b;
      font-size: 11px;
    }
    .datalog-chart-note {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 12px;
      border-top: 1px solid #303030;
      color: #9b9b9b;
      font-size: 11px;
    }
    .datalog-chart-events { border-top: 1px solid #303030; }
    .datalog-chart-events-toggle {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 8px 12px;
      background: none;
      border: none;
      color: #d5d5d5;
      font-size: 12px;
      cursor: pointer;
      text-align: left;
    }
    .datalog-chart-events-toggle:hover { color: #ffffff; }
    .datalog-chart-events-list {
      list-style: none;
      margin: 0;
      padding: 0 12px 8px 12px;
      max-height: 180px;
      overflow-y: auto;
    }
    .datalog-chart-events-list li {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 3px 0;
      font-size: 11px;
      color: #d5d5d5;
    }
    .datalog-chart-events-time {
      min-width: 64px;
      color: #9b9b9b;
      font-family: Consolas, monospace;
    }
    .datalog-chart-events-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex: 0 0 auto;
    }
    li.alert .datalog-chart-events-dot { background: #FF0000; }
    li.comment .datalog-chart-events-dot { background: #2D9CDB; }

    /* Telas pequenas. A altura entra pelo max-height porque um celular deitado
       passa de 640px de largura e mesmo assim so tem ~430px de altura — um
       grafico de 380px tomaria a tela inteira. */
    @media (max-height: 620px) {
      .datalog-chart-canvas { height: min(380px, 48dvh); }
      .datalog-chart-events-list { max-height: 30dvh; }
    }

    @media (max-width: 640px) {
      .datalog-chart-canvas { height: 300px; }
      /* O nome do arquivo empurrava o titulo para fora; ele ja aparece na
         mensagem que trouxe o grafico. */
      .datalog-chart-meta { display: none; }
      .datalog-chart-events-list { max-height: 140px; }
    }

    @media (max-width: 640px) and (max-height: 620px) {
      .datalog-chart-canvas { height: min(300px, 48dvh); }
    }
  `],
})
export class DatalogChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: DatalogChannelsArtifact;
  @ViewChild('chart') chartEl!: ElementRef<HTMLDivElement>;

  expanded = false;

  private instance: echarts.ECharts | null = null;
  private resizeObserver: ResizeObserver | null = null;
  /** Fallback para ambiente sem ResizeObserver (só o resize da janela, como antes). */
  private onWindowResize = () => this.instance?.resize();

  constructor(private transloco: TranslocoService) {}

  /** Atalho para as chaves deste componente (`chatbot.datalogChart.*`). */
  private t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(`chatbot.datalogChart.${key}`, params);
  }

  get channelNames(): string {
    return (this.data?.series ?? []).map((s) => this.labelFor(s.channel)).join(', ');
  }

  /**
   * Rótulo do canal para exibição.
   *
   * Sem tradução, o nome técnico NÃO aparece sozinho (seria vazamento de identificador) —
   * mas também não pode ser omitido: o `name` da série é a identidade da legenda no ECharts,
   * e dois canais sem tradução colidiriam no mesmo nome, quebrando o toggle da legenda (além
   * de o cabeçalho repetir "Canal não identificado" N vezes). Então o identificador vai entre
   * parênteses, como qualificador explícito — não como rótulo.
   */
  labelFor(channel: string): string {
    const r = resolveChannelLabel(channel, currentChannelLocale());
    return r.resolved ? r.label : `${r.label} (${r.raw})`;
  }

  get durationLabel(): string {
    return this.formatTime(this.data?.duration_s ?? 0);
  }

  get markerRows(): { kind: string; time: string; label: string }[] {
    const m = this.data?.markers;
    if (!m?.available) return [];
    const rows = [
      ...m.alerts.map((a) => ({ kind: 'alert', at: a.time, label: this.alertLabel(a) })),
      ...m.comments.map((c) => ({ kind: 'comment', at: c.time, label: this.commentLabel(c) })),
    ].sort((a, b) => a.at - b.at);
    return rows.map((r) => ({ kind: r.kind, time: this.formatTime(r.at), label: r.label }));
  }

  /**
   * Aviso quando os marcadores NÃO puderam ser obtidos (`markers.available === false`).
   * Sem isso, "dado indisponível" e "log sem alertas" ficam indistinguíveis na tela — e
   * afirmar "nenhum alerta" seria dizer algo que o backend não disse.
   * Retorna string vazia quando os marcadores vieram (aí a ausência é informação real).
   */
  get markersUnavailableNote(): string {
    const m = this.data?.markers;
    if (m?.available) return '';
    switch (m?.reason) {
      case 'log-not-converted':
        return this.t('markersLogNotConverted');
      case 'powerft':
        // NÃO afirmar que o formato "não grava" — o front não sabe isso. O que sabemos é
        // que a extração não acontece para PowerFT hoje.
        return this.t('markersPowerft');
      case 'map-only':
        return this.t('markersMapOnly');
      case 'unavailable':
        return this.t('markersUnavailable');
      default:
        // Motivo desconhecido (ou ausente): texto NEUTRO. Afirmar "falha ao carregar" seria
        // dizer algo que o backend não disse.
        return this.t('markersUnknown');
    }
  }

  get eventsSummary(): string {
    const m = this.data.markers;
    const parts: string[] = [];
    if (m.alerts.length) {
      parts.push(`${m.alerts.length} ${this.t(m.alerts.length === 1 ? 'alertOne' : 'alertMany')}`);
    }
    if (m.comments.length) {
      parts.push(`${m.comments.length} ${this.t(m.comments.length === 1 ? 'commentOne' : 'commentMany')}`);
    }
    const suffix = m.truncated ? ` (${this.t('partial')})` : '';
    return `${parts.join(' · ')}${suffix}`;
  }

  private formatTime(t: number): string {
    return `${formatTruncated(t, { minDecimals: 1, maxDecimals: 1, locale: 'en-US', useGrouping: false })}s`;
  }

  get footerNote(): string {
    return datalogFooterNote(
      this.data,
      (key, params) => this.transloco.translate(key, params),
      (channel) => this.labelFor(channel),
    );
  }

  ngAfterViewInit(): void {
    this.instance = echarts.init(this.chartEl.nativeElement);
    this.instance.setOption(this.buildOption());
    this.observeResize();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    window.removeEventListener('resize', this.onWindowResize);
    this.instance?.dispose();
    this.instance = null;
  }

  /**
   * O card muda de largura sem a janela mudar — sidebar abrindo, conversa reaberta do
   * histórico, bolha ainda terminando de aparecer. Observar o container cobre esses casos;
   * o listener de janela só entra onde ResizeObserver não existe.
   */
  private observeResize(): void {
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', this.onWindowResize);
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this.instance?.resize());
    this.resizeObserver.observe(this.chartEl.nativeElement);
  }

  private colorFor(channel: string, idx: number): string {
    if (RPM_CHANNELS.has(baseChannelName(channel))) return RPM_COLOR;
    return CHANNEL_PALETTE[idx % CHANNEL_PALETTE.length];
  }

  /**
   * Ordem, nome e cor das séries — fonte ÚNICA para o gráfico e para o tooltip.
   *
   * Existe por dois motivos:
   * - o gráfico reordena as séries (outros, depois RPM) e a cor deriva desse índice; o tooltip
   *   iterava `data.series` na ordem original, então a bolinha saía com a cor de outra linha;
   * - dois canais podem ter a MESMA tradução (`O2` e `O2_Exaust` → "Sonda geral"). Como o
   *   ECharts usa `name` como identidade da série na legenda, nomes iguais fundem o toggle —
   *   por isso o rótulo repetido é qualificado com o nome técnico.
   */
  private seriesView(): {
    s: DatalogChannelsArtifact['series'][number];
    label: string;
    name: string;
    color: string;
  }[] {
    const rpm = this.data.series.filter((s) => RPM_CHANNELS.has(baseChannelName(s.channel)));
    const others = this.data.series.filter((s) => !RPM_CHANNELS.has(baseChannelName(s.channel)));
    const ordered = [...others, ...rpm];

    const repetidos = new Map<string, number>();
    for (const s of ordered) {
      const l = this.labelFor(s.channel);
      repetidos.set(l, (repetidos.get(l) ?? 0) + 1);
    }

    return ordered.map((s, idx) => {
      const base = this.labelFor(s.channel);
      const label = (repetidos.get(base) ?? 0) > 1 ? `${base} (${s.channel})` : base;
      return {
        s,
        label,
        name: s.unit ? `${label} (${s.unit})` : label,
        color: this.colorFor(s.channel, idx),
      };
    });
  }

  private buildOption(): echarts.EChartsOption {
    const xs = this.data.sample_time;
    const markLineData = this.buildMarkLines();

    const view = this.seriesView();
    const ordered = view.map((v) => v.s);
    const rpm = ordered.filter((s) => RPM_CHANNELS.has(baseChannelName(s.channel)));
    const others = ordered.filter((s) => !RPM_CHANNELS.has(baseChannelName(s.channel)));
    const hasTwoGrids = rpm.length > 0 && others.length > 0;

    const TOP_PCT = 8;
    const BOTTOM_PCT = 22;
    const GAP_PCT = 6;
    const usable = 100 - TOP_PCT - BOTTOM_PCT;
    const topH = hasTwoGrids ? usable * 0.68 - GAP_PCT : usable;
    const bottomH = hasTwoGrids ? usable * 0.32 : 0;

    const grids = hasTwoGrids
      ? [
          { left: 56, right: 56, top: `${TOP_PCT}%`, height: `${topH}%` },
          { left: 56, right: 56, top: `${TOP_PCT + topH + GAP_PCT}%`, height: `${bottomH}%` },
        ]
      : [{ left: 56, right: 56, top: `${TOP_PCT}%`, height: `${usable}%` }];

    const gridOf = (channel: string) => (hasTwoGrids && RPM_CHANNELS.has(baseChannelName(channel)) ? 1 : 0);

    const firstInGrid = new Set<number>();
    const seenGrid = new Set<number>();
    ordered.forEach((s, idx) => {
      const g = gridOf(s.channel);
      if (!seenGrid.has(g)) {
        seenGrid.add(g);
        firstInGrid.add(idx);
      }
    });

    const series = view.map(({ s, name, color }, idx) => {
      const g = gridOf(s.channel);
      return {
        name,
        type: 'line' as const,
        xAxisIndex: g,
        yAxisIndex: idx,
        showSymbol: false,
        connectNulls: false,
        lineStyle: { color, width: 1 },
        itemStyle: { color },
        data: s.data.map((v, i) => [xs[i], v]),
        ...(firstInGrid.has(idx) && markLineData.length
          ? { markLine: { silent: true, symbol: ['none', 'none'], data: markLineData } }
          : {}),
      };
    });

    const xAxes = grids.map((_, g) => ({
      type: 'value' as const,
      gridIndex: g,
      min: 0,
      max: this.data.duration_s,
      axisLine: { lineStyle: { color: '#474747' } },
      axisTick: { show: false },
      axisLabel: {
        show: g === grids.length - 1,
        color: '#9b9b9b',
        fontSize: 10,
        formatter: (v: number) => `${Math.trunc(v)}s`,
      },
      splitLine: { lineStyle: { color: '#252525' } },
    }));

    const yAxes = ordered.map((s, idx) => {
      const color = this.colorFor(s.channel, idx);
      const g = gridOf(s.channel);
      const visible = firstInGrid.has(idx);
      const sameGridBefore = ordered.filter((o, i) => i < idx && gridOf(o.channel) === g).length;
      return {
        type: 'value' as const,
        gridIndex: g,
        scale: true,
        position: sameGridBefore === 0 ? ('left' as const) : ('right' as const),
        offset: sameGridBefore > 1 ? (sameGridBefore - 1) * 42 : 0,
        axisLine: { show: true, lineStyle: { color } },
        axisTick: { show: false },
        axisLabel: { color, fontSize: 10 },
        splitLine: { show: visible, lineStyle: { color: '#252525' } },
      };
    });

    return {
      animation: false,
      backgroundColor: 'transparent',
      textStyle: { color: '#9b9b9b', fontFamily: 'Saira, sans-serif' },
      grid: grids,
      legend: {
        // 'scroll' + largura limitada: rótulo humano é bem mais longo que o nome técnico e,
        // sem isso, a legenda quebra em 2 linhas e cobre o topo do grid (canvas é 380px fixo).
        show: ordered.length > 1,
        type: 'scroll',
        top: 0,
        left: 56,
        right: 56,
        textStyle: { color: '#9b9b9b', fontSize: 11 },
      },
      axisPointer: { link: [{ xAxisIndex: 'all' }] },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'line', label: { formatter: (p: any) => this.formatTime(p.value) } },
        backgroundColor: 'rgba(18, 18, 18, 0.92)',
        borderColor: '#9b9b9b',
        textStyle: { color: '#fff', fontSize: 11 },
        formatter: (params: any) => this.tooltipContent(params),
      },
      xAxis: xAxes,
      yAxis: yAxes.length ? yAxes : [{ type: 'value' as const }],
      dataZoom: [
        { type: 'inside', xAxisIndex: grids.map((_, g) => g), filterMode: 'none' },
        {
          type: 'slider',
          xAxisIndex: hasTwoGrids ? [1, 0] : [0],
          height: 28,
          bottom: 12,
          filterMode: 'none',
          backgroundColor: '#0b0c0e',
          borderColor: '#2a2c31',
          fillerColor: 'rgba(0, 255, 0, 0.18)',
          dataBackground: {
            lineStyle: { color: RPM_COLOR, width: 1 },
            areaStyle: { color: 'rgba(0, 255, 0, 0.15)' },
          },
          selectedDataBackground: {
            lineStyle: { color: RPM_COLOR, width: 1 },
            areaStyle: { color: 'rgba(0, 255, 0, 0.35)' },
          },
          handleStyle: { color: RPM_COLOR, borderColor: RPM_COLOR },
          moveHandleStyle: { color: RPM_COLOR },
          textStyle: { color: '#9b9b9b', fontSize: 10, fontFamily: 'Consolas, monospace' },
        },
      ],
      series,
    } as echarts.EChartsOption;
  }

  /**
   * `name` já vem traduzido do backend. Quando não vem, o alerta é apresentado como não
   * identificado — o `id` é chave de junção com o catálogo de notificações, NÃO texto de
   * usuário, e por isso nunca é exibido.
   */
  alertLabel(alert: { id: number | null; name: string | null }): string {
    return alert.name?.trim() || this.t('alertUnidentified');
  }

  /** Simétrico ao `alertLabel`: comentário sem texto não deve render um marcador vazio. */
  commentLabel(comment: { text: string | null }): string {
    return comment.text?.trim() || this.t('commentEmpty');
  }

  private dot(color: string): string {
    return `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:6px"></span>`;
  }

  private escape(text: string): string {
    return (text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private nearestIndex(x: number): number {
    const xs = this.data.sample_time;
    let lo = 0;
    let hi = xs.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if ((xs[mid] ?? 0) <= x) lo = mid;
      else hi = mid;
    }
    const dLo = Math.abs((xs[lo] ?? 0) - x);
    const dHi = Math.abs((xs[hi] ?? 0) - x);
    return dHi < dLo ? hi : lo;
  }

  private tooltipContent(params: any): string {
    const arr = Array.isArray(params) ? params : [params];
    if (!arr.length) return '';
    const x = arr[0].axisValue as number;
    const i = this.nearestIndex(x);
    const at = this.data.sample_time[i] ?? x;

    const rows = [`<div style="color:#9b9b9b">${this.formatTime(at)}</div>`];
    // Mesma fonte do gráfico: garante que a cor da bolinha bate com a da linha.
    this.seriesView().forEach(({ s, label, color }) => {
      const v = s.data[i];
      const shown = v === null || v === undefined ? '—' : formatTruncated(v, { locale: 'en-US', useGrouping: false });
      const unit = s.unit ? ` ${s.unit}` : '';
      rows.push(`${this.dot(color)}${this.escape(label)}: <b>${shown}${unit}</b>`);
    });

    const near = this.markersNear(at);
    if (near.length) {
      rows.push('<div style="margin-top:4px;border-top:1px solid #3a3a3a;padding-top:4px"></div>');
      near.forEach((mk) => {
        rows.push(`${this.dot(mk.color)}<b>${this.escape(mk.kind)}</b> ${this.formatTime(mk.at)} · ${this.escape(mk.label)}`);
      });
    }
    return rows.join('<br/>');
  }

  private markersNear(x: number): { color: string; kind: string; label: string; at: number }[] {
    const m = this.data.markers;
    if (!m?.available) return [];
    const span = this.data.duration_s / Math.max(1, this.data.points - 1);
    const tol = Math.max(span, this.data.duration_s * 0.004);
    const hits: { color: string; kind: string; label: string; at: number }[] = [];
    m.alerts.forEach((a) => {
      if (Math.abs(a.time - x) <= tol) {
        hits.push({ color: ALERT_COLOR, kind: this.t('alertKind'), label: this.alertLabel(a), at: a.time });
      }
    });
    m.comments.forEach((c) => {
      if (Math.abs(c.time - x) <= tol) {
        hits.push({ color: COMMENT_COLOR, kind: this.t('commentKind'), label: this.commentLabel(c), at: c.time });
      }
    });
    return hits.sort((a, b) => a.at - b.at);
  }

  private buildMarkLines(): any[] {
    const m = this.data.markers;
    if (!m?.available) return [];
    const alerts = m.alerts.map((a) => ({
      xAxis: a.time,
      lineStyle: { color: ALERT_COLOR, width: 1.5, type: 'dashed' as const },
      label: { show: false },
    }));
    const comments = m.comments.map((c) => ({
      xAxis: c.time,
      lineStyle: { color: COMMENT_COLOR, width: 1.5, type: 'dotted' as const },
      label: { show: false },
    }));
    return [...alerts, ...comments];
  }
}
